import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { jwtDecode } from 'jwt-decode';

import Api from '../util/API';
import {
  clearAuthSessionStorage,
  persistAuthSession,
  readAuthSession,
} from '../util/authSessionStorage';

function readString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function apiErrorMessage(err) {
  return (
    readString(err?.response?.data?.message) ||
    readString(err?.response?.data?.error?.message) ||
    readString(err?.message)
  );
}

// Axios fills err.message with 'Request failed with status code NNN' when the
// server body carries no message; never surface that technical string to users.
function isTechnicalErrorText(value) {
  return /request failed with status code|network error|timeout/i.test(String(value || ''));
}

function safeDecodeToken(rawToken) {
  try {
    if (!rawToken || typeof rawToken !== 'string') return null;
    if (rawToken.split('.').length !== 3) return null;
    return jwtDecode(rawToken);
  } catch {
    return null;
  }
}

function normalizeSessionFromApiPayload(payload = {}) {
  return {
    refreshToken: readString(payload.refreshToken),
    sessionExpiresAt: readString(payload.sessionExpiresAt),
    sessionId: readString(payload.sessionId),
    token: readString(payload.token),
  };
}

function normalizeSessionInput(value) {
  if (typeof value === 'string') {
    return {
      refreshToken: '',
      sessionExpiresAt: '',
      sessionId: '',
      token: readString(value),
    };
  }

  return normalizeSessionFromApiPayload(value || {});
}

function assignSessionState(state, session, explicitUser = null, { remember = false } = {}) {
  const decoded = safeDecodeToken(session.token);
  if (!session.token || !decoded) {
    state.token = null;
    state.refreshToken = session.refreshToken || null;
    state.sessionId = session.sessionId || null;
    state.sessionExpiresAt = session.sessionExpiresAt || null;
    state.user = explicitUser || null;
    if (!session.token) {
      clearAuthSessionStorage();
    }
    return;
  }

  state.token = session.token;
  state.refreshToken = session.refreshToken || null;
  state.sessionId = session.sessionId || null;
  state.sessionExpiresAt = session.sessionExpiresAt || null;
  state.user = explicitUser || decoded;
  persistAuthSession(session, { remember });
}

export const loginWithGoogle = createAsyncThunk('auth/loginWithGoogle', async (id_token, { rejectWithValue }) => {
  try {
    const res = await Api.post('/auth/google', { id_token });
    return res.data;
  } catch (err) {
    const message = apiErrorMessage(err);
    return rejectWithValue({
      ...(err.response?.data || {}),
      message: message && !isTechnicalErrorText(message)
        ? message
        : "We couldn't finish Google sign-in. Please try again or sign in with email.",
    });
  }
});

export const loginWithApple = createAsyncThunk('auth/loginWithApple', async (payload, { rejectWithValue }) => {
  try {
    const res = await Api.post('/auth/apple/token', payload);
    return res.data;
  } catch (err) {
    const message = apiErrorMessage(err);
    return rejectWithValue({
      ...(err.response?.data || {}),
      message: message && !isTechnicalErrorText(message)
        ? message
        : "We couldn't finish Apple sign-in. Please try again or sign in with email.",
    });
  }
});

export const loginUser = createAsyncThunk('userAuth/loginUser', async (credentials, thunkAPI) => {
  try {
    const payload = {
      email: credentials?.email,
      password: credentials?.password,
      rememberMe: Boolean(credentials?.rememberMe),
    };
    const res = await Api.post('/user/login', payload);
    return { ...res.data, __rememberMe: payload.rememberMe };
  } catch (err) {
    const status = err?.response?.status;
    const rawMessage = apiErrorMessage(err);
    const apiMessage = rawMessage && !isTechnicalErrorText(rawMessage) ? rawMessage : '';
    // Server copy wins for other statuses (400/403/…), e.g. the 403
    // 'Email not verified with <Provider>…' hint — never genericize it away.
    const friendly = !err.response
      ? "We couldn't reach Cherrfy. Check your connection and try again."
      : status === 429
        ? "You've tried signing in too often. Wait a few minutes."
        : status >= 500
          ? "We're having trouble signing people in right now. Please try again shortly."
          : apiMessage;
    return thunkAPI.rejectWithValue(friendly || 'Sign-in failed');
  }
});

export const registerUser = createAsyncThunk('userAuth/registerUser', async (credentials, thunkAPI) => {
  try {
    const res = await Api.post('/user/register', credentials);
    return res.data;
  } catch (err) {
    const status = err?.response?.status;
    const rawMessage = apiErrorMessage(err);
    const apiMessage = rawMessage && !isTechnicalErrorText(rawMessage) ? rawMessage : '';
    // Server copy wins for other statuses (400/403/…), e.g. the 403
    // 'Email not verified with <Provider>…' hint — never genericize it away.
    const friendly = !err.response
      ? "We couldn't reach Cherrfy. Check your connection and try again."
      : status === 429
        ? "You've tried signing in too often. Wait a few minutes."
        : status >= 500
          ? "We're having trouble right now. Please try again shortly."
          : apiMessage;
    return thunkAPI.rejectWithValue(friendly || 'Registration failed');
  }
});

export const profileInfo = createAsyncThunk('userAuth/profileInfo', async (_, thunkAPI) => {
  try {
    const res = await Api.get('/user/profile');
    return res.data;
  } catch (err) {
    const apiMessage =
      err.response?.data?.message || err.response?.data?.error?.message || err.message;
    return thunkAPI.rejectWithValue(apiMessage || 'Network error');
  }
});

export const logoutUserSession = createAsyncThunk('userAuth/logoutUserSession', async (_, thunkAPI) => {
  try {
    await Api.post('/user/logout', { allDevices: false });
    return true;
  } catch (err) {
    // Logout must continue client-side even if network fails.
    return thunkAPI.rejectWithValue(err.response?.data?.message || err.message || 'Logout failed');
  }
});

const bootstrapSession = normalizeSessionInput(readAuthSession());
const bootstrapUser = safeDecodeToken(bootstrapSession.token);

if (!bootstrapUser && bootstrapSession.token) {
  clearAuthSessionStorage();
}

const initialState = {
  user: bootstrapUser,
  token: bootstrapUser ? bootstrapSession.token : null,
  refreshToken: bootstrapUser ? bootstrapSession.refreshToken || null : null,
  sessionId: bootstrapUser ? bootstrapSession.sessionId || null : null,
  sessionExpiresAt: bootstrapUser ? bootstrapSession.sessionExpiresAt || null : null,
  loading: false,
  error: null,
};

const userAuthSlice = createSlice({
  name: 'userAuth',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    setToken(state, action) {
      const session = normalizeSessionInput(action.payload);
      state.error = null;
      // OAuth handoff (AuthSuccess) always lands in a 30-day persistent
      // session; explicit `remember: false` opts out to sessionStorage.
      assignSessionState(state, session, null, {
        remember: action.payload?.remember !== false,
      });
    },
    setAuthSession(state, action) {
      const session = normalizeSessionInput(action.payload);
      assignSessionState(state, session, state.user || null);
    },
    logout(state) {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.sessionId = null;
      state.sessionExpiresAt = null;
      state.error = null;
      clearAuthSessionStorage();
    },
    checkToken(state) {
      const decoded = safeDecodeToken(state.token);
      if (!decoded) {
        state.user = null;
        state.token = null;
        return;
      }

      if (decoded.exp * 1000 < Date.now()) {
        // Keep refresh session data so interceptor can refresh on demand.
        state.user = decoded;
        return;
      }

      state.user = decoded;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(profileInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(profileInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.data;
        state.error = null;
      })
      .addCase(profileInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Profile info failed';
      })
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const session = normalizeSessionFromApiPayload(action.payload?.data || {});
        assignSessionState(state, session, action.payload?.data?.user || null, {
          remember: Boolean(action.payload?.__rememberMe),
        });
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Sign-in failed';
      })
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const session = normalizeSessionFromApiPayload(action.payload?.data || {});
        // Fresh accounts get the same 30-day persistent session the server
        // issues; sessionStorage would drop them in a new tab.
        assignSessionState(state, session, action.payload?.data?.user || null, {
          remember: true,
        });
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Registration failed';
      })
      .addCase(loginWithGoogle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithGoogle.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const session = normalizeSessionFromApiPayload(action.payload?.data || {});
        // Social sign-ins have no 'remember me' checkbox — the server session
        // is 30 days, so persist to localStorage to match.
        assignSessionState(state, session, action.payload?.data?.user || null, {
          remember: true,
        });
      })
      .addCase(loginWithGoogle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.payload || 'Sign-in failed';
      })
      .addCase(loginWithApple.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithApple.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const session = normalizeSessionFromApiPayload(action.payload?.data || {});
        // Social sign-in has no 'remember me' option; persist the 30-day
        // session in localStorage to match the server-side session lifetime.
        assignSessionState(state, session, action.payload?.data?.user || null, {
          remember: true,
        });
      })
      .addCase(loginWithApple.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.payload || 'Sign-in failed';
      });
  },
});

export const {
  clearError,
  setToken,
  setAuthSession,
  logout,
  checkToken,
} = userAuthSlice.actions;

export default userAuthSlice.reducer;
